import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cpFetch } from '@/lib/cpClient';
import { useRole } from '@/lib/auth';
import { BookOpen, Copy, Filter, Star, Zap } from 'lucide-react';
import type { StepTemplateCategory, VolcanoStepTemplate } from '../../types/volcano.types';

type FilterMode = 'all' | 'free' | 'fast' | 'new';

export interface StepLibraryViewProps {
  /** When provided, show an Insert button and call this with the chosen template. */
  onInsert?: (template: VolcanoStepTemplate) => void;
  /** For read-only surfaces, hide favorites UI */
  hideFavorites?: boolean;
  /** Filter by visibility (internal/external/both). Default: both */
  visibility?: 'internal' | 'external' | 'both';
  /** Optional initial selection (for deep-linking). */
  initialSelectedStepId?: string | null;
}

function categoryLabel(c: StepTemplateCategory) {
  switch (c) {
    case 'foundation':
      return 'Foundation';
    case 'data':
      return 'Data';
    case 'ai':
      return 'AI/LLM';
    case 'integration':
      return 'Integration';
    case 'agricultural':
      return 'Agricultural';
  }
}

function lsGetJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function lsSetJson(key: string, value: any) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export default function StepLibraryView({
  onInsert,
  hideFavorites,
  visibility = 'both',
  initialSelectedStepId = null,
}: StepLibraryViewProps) {
  const { canMutate } = useRole(); // ops/admin

  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState<VolcanoStepTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [selectedCategory, setSelectedCategory] = useState<StepTemplateCategory | 'all'>('all');
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [selectedStepId, setSelectedStepId] = useState<string | null>(initialSelectedStepId);

  const [favorites, setFavorites] = useState<string[]>(() => lsGetJson('dc.volcano.stepFavorites', []));

  type EditorMode = 'create' | 'edit';
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>('create');
  const [editorError, setEditorError] = useState<string | null>(null);

  const emptyDraft = (): VolcanoStepTemplate => ({
    id: '',
    version: '1.0.0',
    scope: 'tenant',
    tenant_id: null,
    visibility: 'both',
    category: 'foundation',
    tags: [],
    flags: { is_new: true, is_popular: false, is_agricultural: false },
    name: '',
    description: '',
    use_cases: [],
    parameters: [],
    documentation: { best_practices: [], common_pitfalls: [], related_step_ids: [] },
    node_type: 'llmStep',
    step: {
      name: 'LLM Step',
      provider: 'anthropic',
      model: 'claude-haiku-4-5-20251001',
      prompt: 'You are a helpful assistant.\n\n{{input}}',
      mcps: [],
      timeout: 30000,
      retry: { strategy: 'exponential', attempts: 3 },
    },
    perf: { timing_ms: 0, cost_usd_estimate: 0 },
    created_at: '',
    updated_at: '',
    created_by: null,
    updated_by: null,
  });

  const [draft, setDraft] = useState<VolcanoStepTemplate>(() => emptyDraft());
  const [draftStepJson, setDraftStepJson] = useState<string>(() => JSON.stringify(emptyDraft().step, null, 2));

  const makeStepSkeleton = (nodeType: VolcanoStepTemplate['node_type']) => {
    if (nodeType === 'llmStep') {
      return {
        name: 'LLM Step',
        provider: 'anthropic',
        model: 'claude-haiku-4-5-20251001',
        prompt: 'You are a helpful assistant.\n\n{{input}}',
        mcps: [],
        timeout: 30000,
        retry: { strategy: 'exponential', attempts: 3 },
      };
    }
    if (nodeType === 'httpStep') {
      return {
        name: 'HTTP Request',
        provider: 'http',
        method: 'GET',
        url: 'https://example.com/api/{{input.id}}',
        headers: { Accept: 'application/json' },
        timeout: 30000,
        retry: { strategy: 'exponential', attempts: 3 },
      };
    }
    if (nodeType === 'toolCall') {
      return {
        name: 'Tool Call',
        provider: 'mcp',
        mcp_server: 'mongodb',
        mcp_tool: 'find',
        input: {},
        timeout: 10000,
      };
    }
    if (nodeType === 'agentCall') {
      return {
        name: 'Agent Call',
        provider: 'agent',
        agent_id: 'contextualizer-registry',
        workflow: 'list_templates',
        input: {},
        timeout: 60000,
      };
    }
    if (nodeType === 'transformStep') {
      return {
        name: 'Transform',
        provider: 'transform',
        transform: { output: '{{input}}' },
      };
    }
    if (nodeType === 'validateStep') {
      return {
        name: 'Validate',
        provider: 'validate',
        schema: { type: 'object', required: [], properties: {} },
        on_error: 'fail',
        input: '{{input}}',
        timeout: 1000,
      };
    }
    if (nodeType === 'notifyStep') {
      return {
        name: 'Notify',
        provider: 'notify',
        channel: 'webhook',
        url: 'https://example.com/webhook',
        payload: { message: 'Workflow complete', input: '{{input}}' },
        timeout: 5000,
      };
    }
    return {};
  };

  const loadSteps = async (preferSelectId?: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await cpFetch(`/api/volcano/steps?scope=all&visibility=${encodeURIComponent(visibility)}`);
      if (!resp?.success) throw new Error(resp?.error || 'Failed to load step library');
      const list = Array.isArray(resp?.steps) ? (resp.steps as VolcanoStepTemplate[]) : [];
      setSteps(list);

      const preferredId = preferSelectId ?? initialSelectedStepId ?? null;
      const preferred = preferredId ? list.find((s) => s.id === preferredId) : null;
      if (preferred) setSelectedStepId(preferred.id);
      else if (!selectedStepId && list.length) setSelectedStepId(list[0].id);
    } catch (e: any) {
      setError(e?.message || 'Failed to load step library');
      setSteps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSteps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibility]);

  const categories = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of steps) counts[s.category] = (counts[s.category] || 0) + 1;
    return (['foundation', 'data', 'ai', 'integration', 'agricultural'] as StepTemplateCategory[]).map((c) => ({
      id: c,
      label: categoryLabel(c),
      count: counts[c] || 0,
    }));
  }, [steps]);

  const filteredSteps = useMemo(() => {
    const q = search.trim().toLowerCase();
    return steps
      .filter((s) => (selectedCategory === 'all' ? true : s.category === selectedCategory))
      .filter((s) => {
        if (!q) return true;
        const hay = `${s.name} ${s.description} ${(s.tags || []).join(' ')}`.toLowerCase();
        return hay.includes(q);
      })
      .filter((s) => {
        if (filterMode === 'all') return true;
        if (filterMode === 'free') return (s.perf?.cost_usd_estimate || 0) <= 0;
        if (filterMode === 'fast') return (s.perf?.timing_ms || 0) > 0 && (s.perf?.timing_ms || 0) <= 500;
        if (filterMode === 'new') return Boolean(s.flags?.is_new);
        return true;
      });
  }, [steps, selectedCategory, search, filterMode]);

  const selected = useMemo(
    () => (selectedStepId ? steps.find((s) => s.id === selectedStepId) || null : null),
    [steps, selectedStepId]
  );

  const canEditSelected = Boolean(canMutate && selected && selected.scope === 'tenant');

  const toggleFavorite = (id: string) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      lsSetJson('dc.volcano.stepFavorites', next);
      return next;
    });
  };

  const copyJson = async (obj: any) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
      toast.success('Copied step JSON to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  const seedDefaults = async () => {
    try {
      setLoading(true);
      const resp = await cpFetch('/api/volcano/steps/seed-defaults', { method: 'POST' });
      if (!resp?.success) throw new Error(resp?.error || 'Failed to seed defaults');
      toast.success(`Seeded ${resp.upserted || 0}/${resp.total || 0} default templates`);
      // Reload step library
      await loadSteps();
    } catch (e: any) {
      if (e?.message === 'forbidden') {
        toast.error('Permission denied. Admin/ops role required to seed defaults.');
      } else {
        toast.error(e?.message || 'Failed to seed defaults');
      }
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    const next = emptyDraft();
    setDraft(next);
    setDraftStepJson(JSON.stringify(next.step, null, 2));
    setEditorMode('create');
    setEditorError(null);
    setEditorOpen(true);
  };

  const openEdit = () => {
    if (!selected) return;
    setDraft({ ...selected });
    setDraftStepJson(JSON.stringify(selected.step || {}, null, 2));
    setEditorMode('edit');
    setEditorError(null);
    setEditorOpen(true);
  };

  const saveDraft = async () => {
    setEditorError(null);

    let stepObj: any;
    try {
      stepObj = JSON.parse(draftStepJson);
    } catch (e: any) {
      setEditorError('Step JSON is invalid. Please fix JSON syntax before saving.');
      return;
    }

    const payload: any = {
      id: String(draft.id || '').trim().toLowerCase(),
      version: String(draft.version || '1.0.0').trim(),
      visibility: draft.visibility,
      category: draft.category,
      tags: Array.isArray(draft.tags) ? draft.tags : [],
      flags: draft.flags || { is_new: false, is_popular: false, is_agricultural: false },
      name: String(draft.name || '').trim(),
      description: String(draft.description || '').trim(),
      use_cases: Array.isArray(draft.use_cases) ? draft.use_cases : [],
      parameters: Array.isArray(draft.parameters) ? draft.parameters : [],
      documentation: draft.documentation || { best_practices: [], common_pitfalls: [], related_step_ids: [] },
      node_type: draft.node_type,
      step: stepObj,
      perf: draft.perf || { timing_ms: 0, cost_usd_estimate: 0 },
      scope: 'tenant',
    };

    if (editorMode === 'create') {
      if (!payload.id) {
        setEditorError('Template id is required.');
        return;
      }
      try {
        setLoading(true);
        const resp = await cpFetch('/api/volcano/steps', { method: 'POST', body: JSON.stringify(payload) });
        if (!resp?.success) throw new Error(resp?.error || 'Failed to create template');
        toast.success('Template created');
        setEditorOpen(false);
        await loadSteps(payload.id);
      } catch (e: any) {
        toast.error(e?.message || 'Failed to create template');
      } finally {
        setLoading(false);
      }
      return;
    }

    // edit
    try {
      setLoading(true);
      const resp = await cpFetch(`/api/volcano/steps/${encodeURIComponent(payload.id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (!resp?.success) throw new Error(resp?.error || 'Failed to update template');
      toast.success('Template updated');
      setEditorOpen(false);
      await loadSteps(payload.id);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update template');
    } finally {
      setLoading(false);
    }
  };

  const deleteSelected = async () => {
    if (!selected) return;
    if (selected.scope !== 'tenant') {
      toast.error('Only tenant templates can be deleted from the UI.');
      return;
    }
    if (!confirm(`Delete template "${selected.name}" (${selected.id})?`)) return;
    try {
      setLoading(true);
      const resp = await cpFetch(`/api/volcano/steps/${encodeURIComponent(selected.id)}`, { method: 'DELETE' });
      if (!resp?.success) throw new Error(resp?.error || 'Failed to delete template');
      toast.success('Template deleted');
      // Remove from favorites if present
      if (favorites.includes(selected.id)) {
        const next = favorites.filter((x) => x !== selected.id);
        setFavorites(next);
        lsSetJson('dc.volcano.stepFavorites', next);
      }
      setEditorOpen(false);
      await loadSteps();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Left: categories */}
      <Card className="col-span-3">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-purple-700" />
              <div className="font-semibold">Step Library</div>
            </div>
            <div className="flex items-center gap-2">
              {canMutate && (
                <Button size="sm" variant="outline" onClick={openCreate} title="Create tenant template (ops/admin)">
                  + New
                </Button>
              )}
              {steps.length === 0 && !loading && (
                <Button size="sm" variant="outline" onClick={seedDefaults} title="Seed default templates (admin only)">
                  <Zap className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              className="w-full justify-between"
              onClick={() => setSelectedCategory('all')}
            >
              <span>All</span>
              <Badge variant="secondary">{steps.length}</Badge>
            </Button>

            {categories.map((c) => (
              <Button
                key={c.id}
                variant={selectedCategory === c.id ? 'default' : 'outline'}
                className="w-full justify-between"
                onClick={() => setSelectedCategory(c.id)}
              >
                <span>{c.label}</span>
                <Badge variant="secondary">{c.count}</Badge>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Middle: catalog */}
      <Card className="col-span-5">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Input placeholder="Search steps..." value={search} onChange={(e) => setSearch(e.target.value)} />
            <Button variant="outline" className="gap-2" onClick={() => setFilterMode('all')}>
              <Filter className="w-4 h-4" />
              {filterMode}
            </Button>
          </div>

          <div className="flex gap-2 mb-3">
            {(['all', 'free', 'fast', 'new'] as FilterMode[]).map((m) => (
              <Button key={m} size="sm" variant={filterMode === m ? 'default' : 'outline'} onClick={() => setFilterMode(m)}>
                {m}
              </Button>
            ))}
          </div>

          <div className="my-2 border-t border-gray-200" />

          {loading && <div className="text-sm text-gray-600">Loading step library…</div>}
          {error && <div className="text-sm text-red-600">{error}</div>}

          {!loading && !error && (
            <div className="h-[60vh] pr-3 overflow-y-auto">
              <div className="space-y-3 pr-1">
                {filteredSteps.map((s) => {
                  const isFav = favorites.includes(s.id);
                  return (
                    <Card
                      key={s.id}
                      className={`cursor-pointer hover:shadow-sm transition ${selectedStepId === s.id ? 'border-purple-400' : ''}`}
                      onClick={() => setSelectedStepId(s.id)}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-semibold text-sm">{s.name}</div>
                            <div className="text-xs text-gray-600 line-clamp-2">{s.description}</div>
                          </div>
                          {!hideFavorites && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(s.id);
                              }}
                              title={isFav ? 'Remove favorite' : 'Add favorite'}
                            >
                              <Star className={`w-4 h-4 ${isFav ? 'text-yellow-500' : 'text-gray-400'}`} />
                            </Button>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <Badge variant="secondary" className="text-xs">
                            {s.category}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {s.node_type}
                          </Badge>
                          {s.flags?.is_new && <Badge className="text-xs">NEW</Badge>}
                          {s.flags?.is_popular && (
                            <Badge variant="secondary" className="text-xs">
                              POPULAR
                            </Badge>
                          )}
                          {s.flags?.is_agricultural && (
                            <Badge variant="outline" className="text-xs">
                              AGR
                            </Badge>
                          )}
                          <span className="text-xs text-gray-500 ml-auto flex items-center gap-1">
                            <Zap className="w-3 h-3" />
                            {s.perf?.timing_ms ?? 0}ms · ${Number(s.perf?.cost_usd_estimate ?? 0).toFixed(3)}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                {filteredSteps.length === 0 && <div className="text-sm text-gray-600">No steps match your filters.</div>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Right: details */}
      <Card className="col-span-4">
        <CardContent className="p-4">
          {!selected && <div className="text-sm text-gray-600">Select a step to view details.</div>}
          {selected && (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold">{selected.name}</div>
                  <div className="text-sm text-gray-600">{selected.description}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    <span className="font-mono">{selected.id}</span> · {selected.scope}
                  </div>
                </div>
                <div className="flex gap-2">
                  {canMutate && (
                    <>
                      <Button
                        variant="outline"
                        onClick={openEdit}
                        disabled={!canEditSelected}
                        title={
                          canEditSelected
                            ? 'Edit tenant template'
                            : 'Only tenant templates are editable (ops/admin only)'
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        onClick={deleteSelected}
                        disabled={!canEditSelected}
                        title={
                          canEditSelected
                            ? 'Delete tenant template'
                            : 'Only tenant templates are deletable (ops/admin only)'
                        }
                      >
                        Delete
                      </Button>
                    </>
                  )}
                  {onInsert && (
                    <Button className="gap-2" onClick={() => onInsert(selected)}>
                      Insert
                    </Button>
                  )}
                  <Button variant="outline" className="gap-2" onClick={() => copyJson(selected.step)}>
                    <Copy className="w-4 h-4" />
                    Copy JSON
                  </Button>
                </div>
              </div>

              <div className="my-4 border-t border-gray-200" />

              {editorOpen && (
                <div className="mb-4 border rounded p-3 bg-white">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm">
                      {editorMode === 'create' ? 'Create Tenant Template' : 'Edit Tenant Template'}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditorOpen(false)}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={saveDraft} disabled={loading}>
                        Save
                      </Button>
                    </div>
                  </div>

                  {editorError && <div className="text-sm text-red-600 mt-2">{editorError}</div>}

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <div className="text-xs font-semibold mb-1">Id</div>
                      <Input
                        value={draft.id}
                        disabled={editorMode === 'edit'}
                        placeholder="e.g. http-get-json"
                        onChange={(e) => setDraft((p) => ({ ...p, id: e.target.value }))}
                      />
                      {editorMode === 'create' && (
                        <div className="text-xs text-gray-500 mt-1">Lowercase letters, digits, dash/underscore.</div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs font-semibold mb-1">Version</div>
                      <Input value={draft.version} onChange={(e) => setDraft((p) => ({ ...p, version: e.target.value }))} />
                    </div>

                    <div className="col-span-2">
                      <div className="text-xs font-semibold mb-1">Name</div>
                      <Input value={draft.name} onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))} />
                    </div>

                    <div className="col-span-2">
                      <div className="text-xs font-semibold mb-1">Description</div>
                      <Input
                        value={draft.description}
                        onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
                      />
                    </div>

                    <div>
                      <div className="text-xs font-semibold mb-1">Category</div>
                      <select
                        className="w-full border rounded px-2 py-2 text-sm"
                        value={draft.category}
                        onChange={(e) => setDraft((p) => ({ ...p, category: e.target.value as any }))}
                      >
                        <option value="foundation">foundation</option>
                        <option value="data">data</option>
                        <option value="ai">ai</option>
                        <option value="integration">integration</option>
                        <option value="agricultural">agricultural</option>
                      </select>
                    </div>

                    <div>
                      <div className="text-xs font-semibold mb-1">Visibility</div>
                      <select
                        className="w-full border rounded px-2 py-2 text-sm"
                        value={draft.visibility}
                        onChange={(e) => setDraft((p) => ({ ...p, visibility: e.target.value as any }))}
                      >
                        <option value="both">both</option>
                        <option value="internal">internal</option>
                        <option value="external">external</option>
                      </select>
                    </div>

                    <div>
                      <div className="text-xs font-semibold mb-1">Node type</div>
                      <select
                        className="w-full border rounded px-2 py-2 text-sm"
                        value={draft.node_type}
                        onChange={(e) => {
                          const nt = e.target.value as any;
                          setDraft((p) => ({ ...p, node_type: nt }));
                          const skel = makeStepSkeleton(nt);
                          setDraftStepJson(JSON.stringify(skel, null, 2));
                        }}
                      >
                        <option value="llmStep">llmStep</option>
                        <option value="httpStep">httpStep</option>
                        <option value="toolCall">toolCall</option>
                        <option value="agentCall">agentCall</option>
                        <option value="transformStep">transformStep</option>
                        <option value="validateStep">validateStep</option>
                        <option value="notifyStep">notifyStep</option>
                      </select>
                    </div>

                    <div>
                      <div className="text-xs font-semibold mb-1">Tags (comma-separated)</div>
                      <Input
                        value={(draft.tags || []).join(', ')}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            tags: e.target.value
                              .split(',')
                              .map((t) => t.trim())
                              .filter(Boolean),
                          }))
                        }
                      />
                    </div>

                    <div className="col-span-2">
                      <div className="flex items-center gap-4 text-sm">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={Boolean(draft.flags?.is_new)}
                            onChange={(e) =>
                              setDraft((p) => ({ ...p, flags: { ...(p.flags || {}), is_new: e.target.checked } as any }))
                            }
                          />
                          New
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={Boolean(draft.flags?.is_popular)}
                            onChange={(e) =>
                              setDraft((p) => ({
                                ...p,
                                flags: { ...(p.flags || {}), is_popular: e.target.checked } as any,
                              }))
                            }
                          />
                          Popular
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={Boolean(draft.flags?.is_agricultural)}
                            onChange={(e) =>
                              setDraft((p) => ({
                                ...p,
                                flags: { ...(p.flags || {}), is_agricultural: e.target.checked } as any,
                              }))
                            }
                          />
                          Agricultural
                        </label>
                      </div>
                    </div>

                    <div className="col-span-2">
                      <div className="text-xs font-semibold mb-1">Step JSON</div>
                      <textarea
                        className="w-full font-mono text-xs border rounded p-2 h-64"
                        value={draftStepJson}
                        onChange={(e) => setDraftStepJson(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="text-sm font-semibold">Use cases</div>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  {(selected.use_cases || []).slice(0, 6).map((u, idx) => (
                    <li key={idx}>{u}</li>
                  ))}
                  {(!selected.use_cases || selected.use_cases.length === 0) && <li className="text-gray-500">No use cases documented yet.</li>}
                </ul>
              </div>

              <div className="my-4 border-t border-gray-200" />

              <div className="space-y-2">
                <div className="text-sm font-semibold">Step JSON</div>
                <pre className="text-xs bg-gray-50 border rounded p-3 overflow-auto max-h-[35vh]">{JSON.stringify(selected.step, null, 2)}</pre>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

